import classnames from 'classnames';
import type { MouseEvent, VoidFunctionComponent } from 'react';

import { plural } from '../../../../shared/helpers/plural';
import { verify_subscription } from '../../../utils';
import Flex from '../../generic/Flex';
import Icon, { icons } from '../../generic/Icon';
import Loader from '../../generic/Loader';
import AlertLimitedScanDepth from '../AlertLimitedScanDepth';
import ButtonRescanProject from '../ButtonRescanProject';
import type { ProjectDashboardProject, ProjectDashboardRevision } from '../projectDashboardTypes';

import ProjectSummaryIssueScanDropdown from './ProjectSummaryIssueScanDropdown';
import type { ScanStatus } from './ProjectSummaryIssues';

type ScanStatusLabelProps = {
  status: ScanStatus;
  issueCount: number;
  isLoading: boolean;
};

// this is the issues scan status that shows up on the header's left side
const ScanStatusLabel: VoidFunctionComponent<ScanStatusLabelProps> = ({
  status,
  issueCount,
  isLoading,
}) => {
  if (isLoading) {
    return <Loader />;
  }

  if (status === 'SUCCEEDED') {
    return (
      <>
        <Icon icon={icons.check} /> All Checks Passed
      </>
    );
  }
  if (status === 'FAILED') {
    return (
      <>
        {issueCount} {plural(issueCount, 'Issue')}
      </>
    );
  }
  return <>Not Scanned Yet</>;
};

type Props = {
  project: ProjectDashboardProject;
  revision: ProjectDashboardRevision;
  issueCount: number;
  status: ScanStatus;
  isLoading: boolean;
  onScanIssues(event: MouseEvent<HTMLElement>): void;
};

const ProjectSummaryIssuesSummaryHeader: VoidFunctionComponent<Props> = ({
  project,
  revision,
  issueCount,
  status,
  onScanIssues,
  isLoading,
}) => {
  const issuesSummaryStatusClassname = classnames('project-summary-header', {
    'project-summary-success': status === 'SUCCEEDED',
    'project-summary-failed': status === 'FAILED',
  });

  return (
    <div className={issuesSummaryStatusClassname}>
      <Flex align="center" justify="space-between">
        <div className="project-summary-header__scan-dropdown">
          <ProjectSummaryIssueScanDropdown revision={revision} />
          <h4>
            <ScanStatusLabel status={status} issueCount={issueCount} isLoading={isLoading} />
            {status !== 'PENDING' &&
              <ButtonRescanProject
                project={project}
                revision={revision}
                onScanIssues={onScanIssues}
              />}
          </h4>
        </div>
        <div className="project-summary-header__vendored-action-buttons text-right">
          {!verify_subscription('premium') && <AlertLimitedScanDepth />}
        </div>
      </Flex>
    </div>);
};

export default ProjectSummaryIssuesSummaryHeader;
